import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { AnimatedBackground } from '@/components/ui/animated-background';
import { Shield, MapPin, FileText, Mail } from 'lucide-react';

export default function SubProcessorsPage() {
  const subprocessors = [
    {
      name: 'Google Cloud Platform (Firestore, Cloud Storage, Secret Manager)',
      service: 'Data storage, encryption at rest, key management',
      regions: ['europe-west2 (London, UK)'],
      notes: 'Stores account metadata, analysis summaries, and fingerprinted cache entries (including short assessment notes with excerpts from the statement). Raw PDFs are not stored.',
    },
    {
      name: 'Google Cloud Functions',
      service: 'Secure execution environment for the ASI scoring pipeline',
      regions: ['europe-west2 (London, UK)'],
      notes: 'Processes uploads, orchestrates analysis, and enforces access controls.',
    },
    {
      name: 'Google Vertex AI (Gemini)',
      service: 'AI model used by the ASI scoring pipeline to evaluate statements',
      regions: ['EU multi-region (EU member states)'],
      notes: 'Content is processed solely to generate the requested analysis and is not retained or used to train Google models.',
    },
    {
      name: 'Plausible Analytics',
      service: 'Cookieless, aggregate analytics',
      regions: ['EU (Plausible Cloud)'],
      notes: 'Receives anonymous event counts only; no PII, filenames, or fingerprints are sent.',
    },
  ];

  return (
    <AnimatedBackground className="w-full">
      <div className="mx-auto max-w-5xl px-6 py-16">
        <div className="mb-12 space-y-6">
          <div className="flex items-center gap-3">
            <Shield className="h-10 w-10 text-[var(--accent)]" />
            <h1 className="font-display text-4xl font-bold !tracking-display text-[var(--text-primary)]">
              Sub-processor Register
            </h1>
          </div>
          <p className="text-lg leading-relaxed text-[var(--text-secondary)]">
            This register lists the infrastructure and service providers that process customer data on behalf of
            Anti-Slavery Intelligence. All processors operate under EU data-protection terms, and no provider is
            permitted to use customer content for advertising or model training.
          </p>
          <div className="rounded-xl border border-[rgba(31,76,84,0.18)] bg-white/70 p-6">
            <p className="text-sm text-[var(--text-secondary)]">
              <strong>Last updated:</strong> October 15, 2026
            </p>
          </div>
        </div>

        <div className="space-y-8">
          <Card className="border-[rgba(31,76,84,0.18)] bg-white/80 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-2xl">
                <FileText className="h-6 w-6 text-[var(--accent)]" />
                Infrastructure and Service Providers
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {subprocessors.map((processor, index) => (
                  <div
                    key={index}
                    className="rounded-xl border border-[rgba(31,76,84,0.12)] bg-[var(--accent-light)]/30 p-6"
                  >
                    <h3 className="mb-3 text-lg font-semibold text-[var(--text-primary)]">
                      {processor.name}
                    </h3>

                    <dl className="space-y-3">
                      <div>
                        <dt className="mb-1 text-sm font-semibold text-[var(--text-secondary)]">
                          Service / role
                        </dt>
                        <dd className="text-[var(--text-secondary)]">{processor.service}</dd>
                      </div>

                      <div>
                        <dt className="mb-2 flex items-center gap-2 text-sm font-semibold text-[var(--text-secondary)]">
                          <MapPin className="h-4 w-4" />
                          Data processing region(s)
                        </dt>
                        <dd className="flex flex-wrap gap-2">
                          {processor.regions.map((region, idx) => (
                            <span
                              key={idx}
                              className="rounded-full bg-[var(--accent)]/10 px-3 py-1 text-sm font-medium text-[var(--accent)]"
                            >
                              {region}
                            </span>
                          ))}
                        </dd>
                      </div>

                      <div>
                        <dt className="mb-1 text-sm font-semibold text-[var(--text-secondary)]">
                          Notes
                        </dt>
                        <dd className="text-[var(--text-secondary)]">{processor.notes}</dd>
                      </div>
                    </dl>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border-[rgba(31,76,84,0.18)] bg-white/80 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-2xl">
                <Shield className="h-6 w-6 text-[var(--accent)]" />
                GDPR Compliance
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3 text-[var(--text-secondary)]">
                <p>
                  All sub-processors listed above are compliant with the EU General Data Protection Regulation (GDPR)
                  and operate under Data Processing Agreements that ensure:
                </p>
                <ul className="ml-6 space-y-2 list-disc">
                  <li>Data is processed only as instructed by Anti-Slavery Intelligence</li>
                  <li>Appropriate technical and organizational security measures are in place</li>
                  <li>Data is stored and processed in London, UK (europe-west2), and AI analysis runs within EU member states, under GDPR-compliant terms</li>
                  <li>Sub-processors do not use customer data for their own purposes</li>
                  <li>Customer data is not used to train AI models or for advertising</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          <Card className="border-[rgba(31,76,84,0.18)] bg-white/80 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-2xl">
                <Mail className="h-6 w-6 text-[var(--accent)]" />
                Updates and Contact
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3 text-[var(--text-secondary)]">
                <p>
                  This register is reviewed and updated whenever a new processor is added, removed, or when there
                  are material changes to existing processors.
                </p>
                <p>
                  For additional contractual details, data processing agreements, or questions about our sub-processors,
                  please contact:
                </p>
                <div className="rounded-xl border border-[rgba(31,76,84,0.12)] bg-[var(--accent-light)]/30 p-4">
                  <a
                    href="mailto:privacy@antislaveryintelligence.co.uk"
                    className="font-semibold text-[var(--accent)] hover:underline"
                  >
                    privacy@antislaveryintelligence.co.uk
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AnimatedBackground>
  );
}
